import React, {
  MutableRefObject,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react'
import { Platform } from 'react-native'

import MusicControl, { Command } from 'react-native-music-control'
import Player from 'react-native-video'

import { PlayerState } from 'src/screens/connected/player/state'
import { buildComponent } from 'src/components/factory'
import { useStoreState } from 'src/hooks/state'
import { getGongDownloadUrl } from 'src/helpers/gong-url'
import { Gong } from 'src/state/models/audio'

import * as Elements from 'src/components/elements'
import AsyncStorage from '@react-native-async-storage/async-storage'

export const AudioPlayer = buildComponent<{
  main: string
  background: string
  cover: string
  title?: string
  album?: string
}>()
  .withStyles({
    player: {
      main: {
        display: 'none',
      },
    },
  })
  .withLifecycle(({ props }) => {
    const Store = PlayerState.useStore()
    const seeked = PlayerState.useStoreState((store) => store.seeked)
    const paused = PlayerState.useStoreState((store) => store.paused)
    const currentTime = PlayerState.useStoreState((store) => store.currentTime)
    const totalTime = PlayerState.useStoreState((store) => store.totalTime)
    const backgroundVolume = PlayerState.useStoreState(
      (store) => store.backgroundVolume,
    )

    // Gong functionality
    const userGong = useStoreState((store) => store.profile.user?.gong)
    const availableGongs = useStoreState((store) => store.audio.gongs)
    const gongTriggeredRef = useRef(false)
    const gongPlayerRef = useRef<Player | null>(null)
    const gongLoadedRef = useRef(false)
    const sessionIdRef = useRef(Date.now()) // Track current session
    const [gongAudioUrl, setGongAudioUrl] = useState<string | null>(null)
    const [gongPaused, setGongPaused] = useState(true)
    const initialBackgroundVolumeRef = useRef<number | null>(null)
    const selectedGongRef = useRef<Gong | undefined>(undefined)
    const gongAudioUrlRef = useRef<string | null>(null)
    const gongTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const [coverImage, setCoverImage] = useState('')

    const started = currentTime > 0
    const hasBackground = !!props.background && props.background.trim() !== ''

    // Find the user's selected gong
    const selectedGong = availableGongs.find(
      (gong: Gong) => gong._id === userGong?._id,
    )
    // Store selectedGong in ref for use in callbacks
    selectedGongRef.current = selectedGong
    const preloadGong = useCallback(async (gongSlug: string) => {
      setGongPaused(true)
      setGongAudioUrl(null)
      gongAudioUrlRef.current = null
      gongLoadedRef.current = false

      try {
        const audioUrl = await getGongDownloadUrl(gongSlug)
        if (selectedGongRef.current?.slug !== gongSlug) {
          return
        }
        setGongAudioUrl(audioUrl)
        gongAudioUrlRef.current = audioUrl
      } catch (error) {
        console.error('Error loading gong for preload:', error)
      }
    }, [])

    const playPreloadedGong = useCallback(() => {
      if (!gongAudioUrl || !gongLoadedRef.current || gongTriggeredRef.current) {
        console.warn('Gong not ready to play or already triggered')
        return
      }

      console.log('Playing preloaded gong')
      console.log('gongAudioUrl:', gongAudioUrl)
      console.log('gongLoadedRef.current:', gongLoadedRef.current)

      // Mark as triggered to prevent replay
      gongTriggeredRef.current = true

      // Play the gong
      setGongPaused(false)
      console.log('Gong paused set to false')

      // Set timeout to stop gong after 10 seconds maximum
      if (gongTimeoutRef.current) {
        clearTimeout(gongTimeoutRef.current)
      }
      gongTimeoutRef.current = setTimeout(() => {
        console.log('Gong timeout: stopping after 10 seconds')
        setGongPaused(true)
        gongTriggeredRef.current = false
        gongLoadedRef.current = false
        gongTimeoutRef.current = null
      }, 10000) // 10 seconds
    }, [gongAudioUrl, gongLoadedRef, gongTriggeredRef])

    const stopGong = useCallback(() => {
      // Clear timeout if it exists
      if (gongTimeoutRef.current) {
        clearTimeout(gongTimeoutRef.current)
        gongTimeoutRef.current = null
      }
      if (gongAudioUrl && !gongTriggeredRef.current) {
        // Only stop gong if it wasn't triggered (playing at end)
        setGongPaused(true)
        console.log('Gong stopped')
      } else if (gongTriggeredRef.current) {
        console.log('Gong is playing at end, not stopping it in stopGong')
      }
    }, [gongAudioUrl])

    // Gong Player callbacks
    const handleGongLoad = useCallback(() => {
      gongLoadedRef.current = true
      console.log('Gong preloaded successfully')
    }, [])

    const handleGongEnd = useCallback(() => {
      console.log('Gong finished playing')
      setGongPaused(true)
      // Clear timeout if it exists
      if (gongTimeoutRef.current) {
        clearTimeout(gongTimeoutRef.current)
        gongTimeoutRef.current = null
      }
      // Clean up gong state after it finishes playing
      gongTriggeredRef.current = false
      gongLoadedRef.current = false
    }, [])

    const handleGongError = useCallback((error: any) => {
      console.error('Error with gong player:', error)
      // Clear timeout if it exists
      if (gongTimeoutRef.current) {
        clearTimeout(gongTimeoutRef.current)
        gongTimeoutRef.current = null
      }
      gongLoadedRef.current = false
      setGongAudioUrl(null)
      gongAudioUrlRef.current = null
      setGongPaused(true)
    }, [])

    // Preload gong when audio starts and reset session
    useEffect(() => {
      if (!selectedGong || !started || gongLoadedRef.current) {
        console.log('Gong not ready to preload')
        console.log('selectedGong:', selectedGong)
        console.log('user.gong:', userGong)
        return
      }

      // Reset gong state for new session
      gongTriggeredRef.current = false
      sessionIdRef.current = Date.now()
      preloadGong(selectedGong.slug)
    }, [selectedGong, started, preloadGong, userGong])

    // Stop gong when audio is paused, but not if gong was triggered (playing at end)
    useEffect(() => {
      if (paused && gongAudioUrl && !gongPaused && !gongTriggeredRef.current) {
        console.log(
          'Stopping gong because audio is paused and gong was not triggered',
        )
        stopGong()
      } else if (paused && gongTriggeredRef.current) {
        console.log('Gong is playing at end, not stopping it')
      }
    }, [paused, gongAudioUrl, gongPaused, stopGong])

    // Reset gong triggered flag only when restarting from the very beginning
    useEffect(() => {
      if (currentTime === 0 && started) {
        // Only reset if we're truly starting over (not just seeking to 0)
        gongTriggeredRef.current = false
        sessionIdRef.current = Date.now()
        console.log('New gong session started')
      }
    }, [currentTime, started])

    // Clean up gong audio on unmount or when gong changes
    useEffect(() => {
      return () => {
        // Clear timeout if it exists
        if (gongTimeoutRef.current) {
          clearTimeout(gongTimeoutRef.current)
          gongTimeoutRef.current = null
        }
        setGongPaused(true)
        setGongAudioUrl(null)
        gongAudioUrlRef.current = null
        gongLoadedRef.current = false
      }
    }, [selectedGong])

    // Store initial background volume when audio starts
    useEffect(() => {
      if (
        started &&
        initialBackgroundVolumeRef.current === null &&
        hasBackground
      ) {
        initialBackgroundVolumeRef.current = backgroundVolume
      }
    }, [started, backgroundVolume, hasBackground])

    // Gradually fade out background music starting 10 seconds before end
    useEffect(() => {
      if (
        !hasBackground ||
        paused ||
        totalTime <= 0 ||
        initialBackgroundVolumeRef.current === null
      ) {
        return
      }

      const timeRemaining = totalTime - currentTime
      const fadeStartTime = 10 // Start fading 10 seconds before end
      const fadeDuration = 10 // Fade over 10 seconds

      if (timeRemaining <= fadeStartTime && timeRemaining > 0) {
        // Calculate fade progress (0 = start of fade, 1 = end of fade)
        const fadeProgress = (fadeStartTime - timeRemaining) / fadeDuration
        // Clamp between 0 and 1
        const clampedProgress = Math.max(0, Math.min(1, fadeProgress))
        // Calculate new volume (from initial volume to 0)
        const newVolume =
          initialBackgroundVolumeRef.current * (1 - clampedProgress)
        Store.getActions().setBackgroundVolume(newVolume)
      } else if (timeRemaining > fadeStartTime) {
        // Restore volume if we're not in the fade zone and volume was modified
        // Only restore if current volume is less than initial (indicating we were fading)
        if (backgroundVolume < initialBackgroundVolumeRef.current) {
          Store.getActions().setBackgroundVolume(
            initialBackgroundVolumeRef.current,
          )
        }
      }
    }, [currentTime, totalTime, paused, hasBackground, backgroundVolume, Store])

    // Reset initial volume ref when audio stops or restarts
    useEffect(() => {
      if (!started) {
        initialBackgroundVolumeRef.current = null
      }
    }, [started])

    // Monitor progress and trigger gong very close to the end (0.5 seconds before)
    useEffect(() => {
      if (
        !selectedGong ||
        paused ||
        totalTime <= 0 ||
        !gongLoadedRef.current ||
        gongTriggeredRef.current
      ) {
        return
      }

      const timeRemaining = totalTime - currentTime
      // Trigger gong when less than 0.5 seconds remain
      const shouldTriggerGong = timeRemaining <= 0.5 && timeRemaining > 0

      if (shouldTriggerGong) {
        console.log('Triggering gong - time remaining:', timeRemaining)
        playPreloadedGong()
      }
    }, [currentTime, totalTime, paused, selectedGong, playPreloadedGong])

    useEffect(() => {
      if (!started) {
        return
      }

      MusicControl.enableControl('play', true)
      MusicControl.enableControl('pause', true)
      MusicControl.enableControl('closeNotification', true, { when: 'paused' })
      MusicControl.enableControl('seek', true)
      MusicControl.enableControl('changePlaybackPosition', true)
      MusicControl.enableControl('setRating', false)
      MusicControl.enableControl('stop', false)
      MusicControl.enableControl('skipForward', false)
      MusicControl.enableControl('skipBackward', false)

      MusicControl.setNowPlaying({
        title: props.title,
        artist: props.album,
        album: 'Nap&Up',
        duration: totalTime,
        artwork: coverImage.includes('://')
          ? coverImage
          : 'file://' + coverImage,
        colorized: true,
      })
    }, [started, props.title, props.album, totalTime, coverImage])

    useEffect(() => {
      if (!started) {
        return
      }

      MusicControl.updatePlayback({
        elapsedTime: currentTime,
        state: paused ? MusicControl.STATE_PAUSED : MusicControl.STATE_PLAYING,
        speed: paused ? 0 : 1,
      })
      // We specifically don't want currentTime to trigger the effect here, only paused state,
      // but we still need to provide currentTime to updatePlayback
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [started, paused])

    useEffect(() => {
      Elements.Image.preloadImage(props.cover).then(setCoverImage)
    }, [props.cover])

    useEffect(() => {
      MusicControl.enableBackgroundMode(true)
      if (Platform.OS === 'ios') {
        MusicControl.handleAudioInterruptions(true)
      }

      MusicControl.on(Command.play, () => {
        Store.getActions().setPaused(false)
      })
      MusicControl.on(Command.pause, () => {
        Store.getActions().setPaused(true)
      })
      MusicControl.on(Command.togglePlayPause, () => {
        Store.getActions().toggle(undefined)
      })
      MusicControl.on(Command.seek, (value: number) => {
        MusicControl.updatePlayback({
          elapsedTime: value,
        })

        Store.getActions().seek(value)
      })
      MusicControl.on(Command.changePlaybackPosition, (value: string) => {
        Store.getActions().seek(Number(value))
      })

      return () => {
        MusicControl.enableBackgroundMode(false)
        MusicControl.resetNowPlaying()
      }
    }, [Store])

    useEffect(() => {
      AsyncStorage.getItem('backgroundVolume')
        .then((v) => v && Store.getActions().setBackgroundVolume(+v))
        .catch(() => {})
    }, [Store])

    return {
      paused,
      currentTime,
      totalTime,
      backgroundVolume,
      hasBackground,
      gongAudioUrl,
      gongPaused,
      gongPlayerRef,
      handleGongLoad,
      handleGongEnd,
      handleGongError,
      setCurrentTime: (value: number) => {
        if (seeked) {
          Store.getActions().setSeeked(false)
        } else {
          Store.getActions().setCurrentTime(value)
        }
      },
      setTotalTime: Store.getActions().setTotalTime,
      setMainPlayer: Store.getActions().setMainPlayer,
      setBackgroundPlayer: Store.getActions().setBackgroundPlayer,
      toggle: Store.getActions().toggle,
      stop: () => {
        Store.getActions().setCurrentTime(0)
        Store.getActions().setPaused(true)
        MusicControl.resetNowPlaying()
        // Clean up gong audio when stopping
        // Clear timeout if it exists
        if (gongTimeoutRef.current) {
          clearTimeout(gongTimeoutRef.current)
          gongTimeoutRef.current = null
        }
        stopGong()
        setGongPaused(true)
        setGongAudioUrl(null)
        gongAudioUrlRef.current = null
        gongTriggeredRef.current = false
        gongLoadedRef.current = false
        sessionIdRef.current = Date.now()
        // Restore initial background volume
        if (initialBackgroundVolumeRef.current !== null) {
          Store.getActions().setBackgroundVolume(
            initialBackgroundVolumeRef.current,
          )
          initialBackgroundVolumeRef.current = null
        }
      },
      handleMainAudioEnd: () => {
        console.log('Main audio ended')
        console.log('gongTriggeredRef.current:', gongTriggeredRef.current)
        console.log('gongPaused:', gongPaused)
        // When main audio ends, stop it but don't stop the gong if it's playing
        Store.getActions().setCurrentTime(0)
        Store.getActions().setPaused(true)
        MusicControl.resetNowPlaying()
        // Don't stop gong here - let it finish playing
        // Restore initial background volume
        if (initialBackgroundVolumeRef.current !== null) {
          Store.getActions().setBackgroundVolume(
            initialBackgroundVolumeRef.current,
          )
          initialBackgroundVolumeRef.current = null
        }
      },
    }
  })
  .withRender(({ props, lifecycle, styles }) => (
    <>
      <Player
        audioOnly
        ref={lifecycle.setMainPlayer as unknown as MutableRefObject<Player>}
        source={{ uri: props.main }}
        paused={lifecycle.paused}
        playInBackground
        ignoreSilentSwitch="ignore"
        playWhenInactive
        style={styles.player()}
        onProgress={(data) => {
          lifecycle.setCurrentTime(data.currentTime)
          lifecycle.setTotalTime(data.seekableDuration)
        }}
        onEnd={lifecycle.handleMainAudioEnd}
      />
      {lifecycle.hasBackground && (
        <Player
          audioOnly
          ref={
            lifecycle.setBackgroundPlayer as unknown as MutableRefObject<Player>
          }
          paused={lifecycle.paused}
          source={{ uri: props.background }}
          playInBackground
          ignoreSilentSwitch="ignore"
          playWhenInactive
          style={styles.player()}
          volume={lifecycle.backgroundVolume}
        />
      )}
      {/* Hidden Gong Player for end-of-session gong */}
      {lifecycle.gongAudioUrl && (
        <Player
          ref={lifecycle.gongPlayerRef}
          source={{ uri: lifecycle.gongAudioUrl }}
          audioOnly
          paused={lifecycle.gongPaused}
          volume={0.8}
          style={styles.player()}
          onLoad={lifecycle.handleGongLoad}
          onEnd={lifecycle.handleGongEnd}
          onError={lifecycle.handleGongError}
          playInBackground={false}
          ignoreSilentSwitch="ignore"
        />
      )}
    </>
  ))
